import { Injectable, OnDestroy } from "@angular/core";
import { Subscription, Subject, Observable } from "rxjs";
import { GlobalFunction } from "../global-function";
import { HttpClient } from "@angular/common/http";
import { TutorialComponent, TutorialStep } from "../models/tutorial/tutorial.model";
import { Router } from "@angular/router";
import { AppConfigService } from "./app-config.service";
import { AuthResponseData } from "../models/authentication/auth-response-data";


@Injectable({
    providedIn: 'root'
})
export class TutorialService implements OnDestroy
{


    //#region general
    private subscriptions: Subscription[] = [];

    private tutorialComponents: TutorialComponent[] = [];

    private userRoles: string[] = [];
    public completedTutorials: string[] = [];
    //#endregion


    //#region tutorial management
    // this object will act as a hashtable. It will contain the name of the tutorialComponent for each route. 
    // Example: tutorialRouteRelation['/my-appointments'] = 'my-appointments'
    private readonly tutorialRouteRelation: Object = new Object();

    public readonly showTutorialTrigger: Subject<string> = new Subject<string>();
    //#endregion

    constructor(private readonly http: HttpClient, private router: Router)
    {
        this.userRoles = GlobalFunction.getUserRoles();
        this.setupTutorialRouteRelations();
        this.generateTutorialComponents();
    }


    public getCompletedTutorials(): Observable<string[]>
    {

        let authResponseData: AuthResponseData;
        if (!GlobalFunction.isEmpty(localStorage.getItem('AppointmentManagementUser')))
        {
            authResponseData = JSON.parse(localStorage.getItem('AppointmentManagementUser'));
        }
        return this.http.get<string[]>(AppConfigService.appConfig.WebAPIServer + '/api/employee/get-completed-tutorials/' + authResponseData.userId);
    }

    public addCompletedTutorial(name: string): Observable<any>
    {

        let authResponseData: AuthResponseData;
        if (!GlobalFunction.isEmpty(localStorage.getItem('AppointmentManagementUser')))
        {
            authResponseData = JSON.parse(localStorage.getItem('AppointmentManagementUser'));
        }


        return this.http.get<string>(AppConfigService.appConfig.WebAPIServer + '/api/employee/add-completed-tutorial/' + authResponseData.userId + '/name/' + name);


    }


    public hasCompletedTutorial(): boolean
    {
        const tutorialToShow: string = this.tutorialRouteRelation[this.router.url] as string;
        if (!GlobalFunction.isEmpty(tutorialToShow))
        {
            if (this.completedTutorials.indexOf(tutorialToShow) === -1)
            {
                return false;
            }
            else
            {
                return true;
            }
        }
        else
        {
            return false;
        }
    }

    public showTutorial(): void
    {

        const tutorialToShow: string = this.tutorialRouteRelation[this.router.url] as string;
        if (!GlobalFunction.isEmpty(tutorialToShow))
        {

            const tutorialComponent = this.tutorialComponents.filter(comp => comp.name == tutorialToShow)[0];
            if (!GlobalFunction.isEmpty(tutorialComponent))
            {
                tutorialComponent.tutorialCompleted = false;
                this.showTutorialTrigger.next(tutorialToShow);
            }
        }

    }

    public getTutorialComponent(name: string): TutorialComponent
    {
        let componentList = this.tutorialComponents.filter(comp => comp.name == name);
        if (GlobalFunction.isEmpty(componentList))
        {
            return null;
        }

        let component = { ...componentList[0] };

        if (component.tutorialCompleted)
        {
            return null;
        }

        component.tutorialSteps = this.getComponentSteps(component);
        return component;
    }

    private getComponentSteps(component: TutorialComponent): TutorialStep[]
    {
        let result: TutorialStep[] = []

        component.tutorialSteps.forEach(step => 
        {
            let requirementsFullfilled: boolean = true;
            step.authorizationRequirements.forEach(requirement =>
            {
                if (this.userRoles.indexOf(requirement) === -1)
                {
                    requirementsFullfilled = false;
                }
            });
            if (requirementsFullfilled)
            {
                result.push(step);
            }
        }
        );

        return result;
    }

    public endTutorialComponent(name: string): void
    {
        let componentList = this.tutorialComponents.filter(comp => comp.name == name);
        if (GlobalFunction.isEmpty(componentList))
        {
            return null;
        }

        let component = componentList[0];
        component.tutorialCompleted = true;
        if (this.completedTutorials.indexOf(name) == -1)
        {
            this.addCompletedTutorial(name).subscribe(
                {
                    next: () => {},
                    error: (error) => {
                        console.error(error);
                    }
                }
            );
        }
    }


    //#region tutorial setup
    private setupTutorialRouteRelations(): void
    {
        this.tutorialRouteRelation['/my-appointments'] = "my-appointments";
    }

    private generateTutorialComponents(): void 
    {
        this.myAppointmentsTutorial();
        this.createPersonalSettingsTutorial();
    }

    //#region my-appointments
    private myAppointmentsTutorial(): void 
    {
        const component: TutorialComponent = {
            name: "my-appointments",
            tutorialCompleted: false,
            tutorialSteps: []
        };

        const stepIntro: TutorialStep = {
            name: "OrgIntro",
            authorizationRequirements: []
        };
        component.tutorialSteps.push(stepIntro);

        const stepTutorial: TutorialStep = {
            name: "Tutorial",
            authorizationRequirements: []
        };
        component.tutorialSteps.push(stepTutorial);

        const stepMonth: TutorialStep = {
            name: "CalendarMonthOverview",
            authorizationRequirements: []
        };
        component.tutorialSteps.push(stepMonth);

        const stepWeek: TutorialStep = {
            name: "CalendarWeekOverview",
            authorizationRequirements: []
        };
        component.tutorialSteps.push(stepWeek);

        const stepCurrentDate: TutorialStep = {
            name: "CalendarCurrentDate",
            authorizationRequirements: []
        };
        component.tutorialSteps.push(stepCurrentDate);

        const stepNavigation: TutorialStep = {
            name: "CalendarNavigation",
            authorizationRequirements: []
        };
        component.tutorialSteps.push(stepNavigation);

        const stepBookPersonal: TutorialStep = {
            name: "CalendarAddPersonal",
            authorizationRequirements: ["MyAppointments.Add"]
        };
        component.tutorialSteps.push(stepBookPersonal);

        const stepBookAny: TutorialStep = {
            name: "CalendarAddOther",
            authorizationRequirements: ["AllAppointments.Add"]
        };
        component.tutorialSteps.push(stepBookAny);

        const stepOtherCalendars: TutorialStep = {
            name: "OtherCalendars",
            authorizationRequirements: []
        };
        component.tutorialSteps.push(stepOtherCalendars);

        const stepUserSettings: TutorialStep = {
            name: "UserSettings",
            authorizationRequirements: []
        };
        component.tutorialSteps.push(stepUserSettings);

        this.tutorialComponents.push(component);
    }
    //#endregion end calendar

    //#region personal settings
    private createPersonalSettingsTutorial(): void
    {
        const component: TutorialComponent = {
            name: "PersonalSettings",
            tutorialCompleted: false,
            tutorialSteps: []
        };

        const step1: TutorialStep = {
            name: "CalendarSettings",
            authorizationRequirements: []
        };
        component.tutorialSteps.push(step1);

        this.tutorialComponents.push(component);
    }
    //#endregion

    //#endregion end tutorial


    ngOnDestroy()
    {
        this.subscriptions.forEach(s => s.unsubscribe());
    }
}
